import React from 'react'
import { Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSettingsStore } from '@/store/settings.store'
import { setDocumentDirection } from '@/lib/rtl'

interface Language {
  code: string
  name: string
  nativeName: string
  flag: string
}

/**
 * Language toggle component that switches i18n language and document direction
 * Supports both LTR and RTL languages with automatic direction switching
 */
export const LangToggle: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const { language, setLanguage } = useSettingsStore()

  const languages: Language[] = [
    {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      flag: '🇺🇸',
    },
    {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      flag: '🇸🇦',
    },
  ]

  const handleLanguageChange = async (langCode: string) => {
    try {
      // Update i18next language
      await i18n.changeLanguage(langCode)
      
      // Update settings store (this will also update document direction)
      setLanguage(langCode as 'en' | 'ar')
      
      // Ensure document direction is updated
      setDocumentDirection(langCode)
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
          <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
            <Languages className="h-3 w-3 text-white" />
          </div>
          <span className="sr-only">{t('language.switchLanguage')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={language === lang.code ? 'bg-accent' : ''}
          >
            <span className="mr-2 text-base">{lang.flag}</span>
            <div className="flex flex-col">
              <span className="text-sm font-medium">{lang.name}</span>
              <span className="text-xs text-muted-foreground">{lang.nativeName}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 